// libs
import React, { useEffect, useMemo, useRef } from 'react';
import { shallow } from 'zustand/shallow';
// widgets
import Tile from '@/widgets/Tile/Tile';
import LocationPref from '@/pages/instamart/campaign/create/widgets/Steps/AdPreference/widgets/Preferences/Location';
import SchedulePref from '@/pages/instamart/campaign/create/widgets/Steps/AdPreference/widgets/Preferences/Schedule';
// styles
import style from './AdPreference.module.scss';
// components
import TabsNavigator from '@/components/TabsNavigator/TabsNavigator';
// constants
import { IconClock, IconGlobe } from '@/constants/icons/svg';
import { DlsColorTokens, DlsFontVariants } from '@/constants/DLSTokens';
import { CampaignWriteModeEnum } from '@/stores/WriteCampaign/constants';
import { CampaignStatus } from '@local/typedefs/dist/protos/campaign/Campaign';
// dls
import { Text } from '@swiggy-private/dls-web-core/dist/components/Text';
// stores
import useWriteCampaignStore from '@/stores/WriteCampaign';
import useConfigStore from '@/stores/Configs';
const AdPreference = ({ mode, currentCampaign }) => {
    const tabsNavRef = useRef(null);
    const { cityIds, setCityIds, daysOfWeek, setDaysOfWeek, mealSlots, setMealSlots, campaignStartDate, setCampaignStartDate, campaignEndDate, setCampaignEndDate, setAdGroupCityIds, validationMessages, isValid, } = useWriteCampaignStore((store) => ({
        cityIds: store.cityIds,
        setCityIds: store.setCityIds,
        daysOfWeek: store.adGroups[0].daysOfWeek,
        setDaysOfWeek: store.setAdGroupDaysOfWeek,
        mealSlots: store.adGroups[0].mealSlots,
        setMealSlots: store.setAdGroupMealSlots,
        campaignStartDate: store.startDate,
        setCampaignStartDate: store.setStartDate,
        campaignEndDate: store.endDate,
        setCampaignEndDate: store.setEndDate,
        setAdGroupCityIds: store.setAdGroupCityIds,
        validationMessages: store.validationMessages,
        isValid: store.isValid,
    }), shallow);
    const cityOptions = useConfigStore((store) => store.cities);
    const campaignStatus = mode === CampaignWriteModeEnum.EDIT && currentCampaign
        ? currentCampaign.status
        : CampaignStatus.CAMPAIGN_STATUS_INVALID;
    useEffect(() => {
        const haveLocationErrors = !!validationMessages['cityIds'];
        const haveScheduleErrors = !!(validationMessages['startDate'] ||
            validationMessages['endDate'] ||
            validationMessages['daysOfWeek'] ||
            validationMessages['mealSlots']);
        if (!tabsNavRef.current) {
            return;
        }
        const currentTabId = tabsNavRef.current.getActiveTabId();
        if (currentTabId === 'location' &&
            !haveLocationErrors &&
            haveScheduleErrors) {
            tabsNavRef.current.setActiveTabId('schedule');
        }
        else if (currentTabId === 'schedule' &&
            haveLocationErrors &&
            !haveScheduleErrors) {
            tabsNavRef.current.setActiveTabId('location');
        }
    }, [validationMessages]);
    const handleSelectedCityIdsUpdate = (ids) => {
        if (campaignStatus === CampaignStatus.CAMPAIGN_STATUS_LIVE) {
            return;
        }
        setCityIds(ids);
        setAdGroupCityIds(ids);
        isValid(['cityIds']);
    };
    const handleDaysOfWeekUpdate = (days) => {
        setDaysOfWeek(days);
        isValid(['daysOfWeek']);
    };
    const handleMealSlotsUpdate = (slots) => {
        setMealSlots(slots);
        isValid(['mealSlots']);
    };
    const handleCampaignStartDateUpdate = (date) => {
        setCampaignStartDate(date);
        isValid(['startDate']);
    };
    const handleCampaignEndDateUpdate = (date) => {
        setCampaignEndDate(date);
        isValid(['endDate']);
    };
    const locationProps = {
        campaignStatus,
        cityOptions,
        cityIds,
        setCityIds: handleSelectedCityIdsUpdate,
        validationMessages,
        mode,
    };
    const scheduleProps = {
        campaignStatus,
        currentCampaignEndDate: currentCampaign === null || currentCampaign === void 0 ? void 0 : currentCampaign.endDate,
        daysOfWeek,
        setDaysOfWeek: handleDaysOfWeekUpdate,
        mealSlots,
        setMealSlots: handleMealSlotsUpdate,
        campaignStartDate,
        setCampaignStartDate: handleCampaignStartDateUpdate,
        campaignEndDate,
        setCampaignEndDate: handleCampaignEndDateUpdate,
        validationMessages,
    };
    const config = useMemo(() => [
        {
            id: 'location',
            tabLabel: 'Location',
            Content: LocationPref,
            contentProps: locationProps,
            tabOptions: {
                Icon: IconGlobe,
            },
        },
        {
            id: 'schedule',
            tabLabel: 'Schedule',
            Content: SchedulePref,
            contentProps: scheduleProps,
            tabOptions: {
                Icon: IconClock,
            },
        },
    ], [scheduleProps, locationProps]);
    return (React.createElement(React.Fragment, null,
        React.createElement(Tile, { className: style.tile },
            React.createElement("div", { className: style.content },
                React.createElement("div", { className: style.contentHeader },
                    React.createElement(Text, { className: style.contentHeaderTitle, variant: DlsFontVariants.button_Text_BT2, color: DlsColorTokens.text_Highest_Emphasis }, "Target the users your Ad would be shown to"),
                    React.createElement(Text, { variant: DlsFontVariants.body_B1_Reg, color: DlsColorTokens.text_Med_Emphasis }, "Your targeting helps us define the user sets we show your Ads to.")),
                React.createElement(TabsNavigator, { config: config, ref: tabsNavRef })))));
};
export default AdPreference;
